import { Button, Card, Descriptions, Divider, Drawer, Empty, Message, Select, Space, Spin, Table, Tag } from "@arco-design/web-react";
import { IconEye, IconRefresh } from "@arco-design/web-react/icon";
import OperationButton from "@/components/OperationButton";
import { useCallback, useEffect, useRef, useState } from "react";
import { CompanyAPI } from "@/apis/system";
import { BankAPI } from "@/apis/banking";
import { DateTime } from "@/components/DateTime";
import { Currency } from "@/components/Numeric";
import _ from "lodash";

const STATUS_TAG_MAP = {
    ACTIVE: { color: 'green', text: 'Active' },
    DELETED: { color: 'gray', text: 'Deleted' },
    REMOVE_FAILED: { color: 'red', text: 'Remove Failed' },
};

const STATUS_OPTIONS = [
    { label: 'Active', value: 0 },
    { label: 'Deleted', value: 1 },
    { label: 'Remove Failed', value: 2 },
];

const ACCOUNT_STATUS_TAG_MAP = {
    true: { color: 'gray', text: 'Deleted' },
    false: { color: 'green', text: 'Active' },
};

export const BankConnections = () => {
    const [companies, setCompanies] = useState([]);
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [companyId, setCompanyId] = useState(undefined);
    const [status, setStatus] = useState(undefined);
    const [retryingId, setRetryingId] = useState(undefined);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [activeConnection, setActiveConnection] = useState(null);
    const [detailData, setDetailData] = useState({ accounts: [] });
    // 请求序号：快速连续打开不同连接时丢弃过期响应，避免详情数据错乱
    const detailReqRef = useRef(0);

    const getCompanies = useCallback(async () => {
        const companyList = await CompanyAPI.active();
        setCompanies(_.map(companyList, (item) => ({
            label: item.companyName,
            value: item.id
        })));
    }, []);

    const getConnections = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, pageSize: 20 };
            if (companyId) params.companyId = companyId;
            if (status != null) params.status = status;
            const res = await BankAPI.getConnections(params);
            setConnections(res.records);
            setTotal(res.total);
        } finally {
            setLoading(false);
        }
    }, [page, companyId, status]);

    useEffect(() => {
        getCompanies();
    }, [getCompanies]);

    useEffect(() => {
        getConnections();
    }, [getConnections]);

    const retryRemove = async (bankId) => {
        try {
            setRetryingId(bankId);
            await BankAPI.retryRemove(bankId);
            Message.success("Plaid item removed successfully");
            getConnections();
        } catch (error) {
            Message.error(error.message);
        } finally {
            setRetryingId(undefined);
        }
    };

    const renderStatusTag = (status) => {
        const info = STATUS_TAG_MAP[status] || { color: 'gray', text: status };
        return <Tag color={info.color}>{info.text}</Tag>;
    };

    const openDetail = async (record) => {
        const reqId = ++detailReqRef.current;
        setActiveConnection(record);
        setDetailData({ accounts: [] });
        setDrawerVisible(true);
        setDetailLoading(true);
        try {
            const accounts = await BankAPI.connectionAccounts(record.id);
            if (detailReqRef.current !== reqId) return;
            setDetailData({ accounts });
        } catch (error) {
            if (detailReqRef.current === reqId) Message.error(error.message);
        } finally {
            if (detailReqRef.current === reqId) setDetailLoading(false);
        }
    };

    const closeDetail = () => setDrawerVisible(false);

    const columns = [
        {
            title: "Company Name", dataIndex: "companyName", width: 180,
        },
        {
            title: "Bank", dataIndex: "institutionName", width: 160,
        },
        {
            title: "Status", dataIndex: "status", width: 130,
            render: (val) => renderStatusTag(val),
        },
        {
            title: "Accounts", dataIndex: "activeAccountCount", width: 110,
            render: (val, record) => `${val}/${record.totalAccountCount}`,
        },
        {
            title: "Operation", width: 110,
            render: (val, record) => (
                <Space>
                    {record.status === 'REMOVE_FAILED' && (
                        <OperationButton icon={<IconRefresh />} label="Retry" status="warning"
                            confirmTitle="Retry Plaid Removal"
                            confirmContent="Retry removing this item from Plaid?"
                            onConfirm={() => retryRemove(record.id)}
                            loading={retryingId === record.id} />
                    )}
                    <OperationButton icon={<IconEye />} label="Details"
                        onClick={() => openDetail(record)} />
                </Space>
            ),
        },
    ];

    const accountColumns = [
        {
            title: "Account Name", dataIndex: "institutionAccountName",
        },
        {
            title: "Type", dataIndex: "type",
            render: (val, record) => [record.type, record.subType]
                .filter(Boolean).map((type) => _.capitalize(type)).join(" / ") || "-",
        },
        {
            title: "Mask", dataIndex: "mask",
            render: (val) => val ? `**** ${val}` : "-",
        },
        {
            title: "Balance", dataIndex: "balance",
            render: (val) => val != null ? <Currency value={val} /> : "-",
        },
        {
            title: "Auto Sync", dataIndex: "autoTransSync",
            render: (val) => val ? "Yes" : "No",
        },
        {
            title: "Sync Start Date", dataIndex: "syncStartDate",
            render: (val) => val || "-",
        },
        {
            title: "Last Sync", dataIndex: "lastTransSync",
            render: (val) => val ? <DateTime value={val} /> : "-",
        },
        {
            title: "Status", dataIndex: "deleted",
            render: (val) => {
                const info = ACCOUNT_STATUS_TAG_MAP[Boolean(val)];
                return <Tag color={info.color}>{info.text}</Tag>;
            },
        },
    ];

    // 删除信息以 bank 表为唯一来源（bank_audit 不再写入）：Delete Time 为本地软删除时刻，Request ID 为 Plaid 移除成功后的回写值
    const detailItems = activeConnection ? [
        { label: "Company Name", value: activeConnection.companyName },
        { label: "Bank", value: activeConnection.institutionName },
        { label: "Institution ID", value: activeConnection.institutionId || "-" },
        { label: "Status", value: renderStatusTag(activeConnection.status) },
        {
            label: "Create Time",
            value: activeConnection.createTime ? <DateTime value={activeConnection.createTime} /> : "-",
        },
        {
            label: "Delete Time",
            value: activeConnection.deleteTime ? <DateTime value={activeConnection.deleteTime} /> : "-",
        },
        { span: 2, label: "Request ID", value: activeConnection.itemRemoveRequestId || "-" },
        { span: 2, label: "Access Token", value: activeConnection.maskedAccessToken || "-" },
    ] : [];

    return (
        <Card title="Bank Connections" extra={
            <Space>
                <Select options={STATUS_OPTIONS}
                    style={{ width: 160 }}
                    placeholder="Status"
                    allowClear
                    onChange={(val) => { setPage(1); setStatus(val); }} />
                <Select options={companies}
                    style={{ width: 200 }}
                    placeholder="Company"
                    allowClear
                    showSearch
                    filterOption={(inputValue, option) =>
                        option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                    }
                    onChange={(val) => { setPage(1); setCompanyId(val); }} />
            </Space>
        }>
            <Table columns={columns} rowKey="id" data={connections} loading={loading}
                pagination={{ total, current: page, pageSize: 20 }}
                onChange={(pagination) => setPage(pagination.current)} />
            <Drawer width={800} title="Connection Details"
                visible={drawerVisible}
                footer={<Button type="primary" onClick={closeDetail}>Close</Button>}
                onCancel={closeDetail}>
                {detailLoading ? (
                    <div style={{ padding: 48, textAlign: "center" }}>
                        <Spin />
                    </div>
                ) : (
                    <>
                        <Descriptions column={2} colon=':' labelStyle={{ paddingRight: 36 }}
                            data={detailItems} />
                        <Divider orientation="left" style={{ margin: "16px 0 8px" }}>Accounts</Divider>
                        {detailData.accounts.length > 0 ? (
                            <Table rowKey="id" columns={accountColumns} data={detailData.accounts}
                                pagination={false} size="small" />
                        ) : (
                            <Empty description="No accounts" style={{ margin: "16px 0" }} />
                        )}
                    </>
                )}
            </Drawer>
        </Card>
    )
}
